import React from 'react'
import urljoin from 'url-join'

import AttributionContextProvider from 'shared/components/common/attribution_context/AttributionContext'
import RestaurantContextProvider, {
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import { RestaurantRoutesContextProvider } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { RxDataUsage } from 'shared/js/hooks/useDefaultSiteData'

import { PageType } from 'src/apollo/sites'
import { DebugContextProvider } from 'src/shared/components/common/debug/DebugContext'
import { GlobalNavContextProvider } from 'src/public/components/default_template/GlobalNavContext'
import { SpotlightContextProvider } from 'src/public/components/default_template/spotlight/SpotlightContext'
import { ReservationContextProvider } from 'src/public/components/online_ordering/ReservationContext'
import LocationSelectionGate from 'public/components/default_template/online_ordering/location_selection/LocationSelectionGate'
import { EcommPageContexts } from 'src/public/components/default_template/online_ordering/pages/EcommPageContexts'

type Props = {
  ecommPath?: string
  ecommCheckoutPathPrefix?: string
  ecommConfirmPathPrefix?: string
}

/**
 * Inner component that computes the adjusted ecommPath for multi-location support.
 * Must be inside RestaurantContextProvider to access multi-location settings.
 */
const EcommerceRoutesProvider = ({
  ecommPath,
  ecommCheckoutPathPrefix,
  ecommConfirmPathPrefix,
  children
}: React.PropsWithChildren<{
  ecommPath?: string
  ecommCheckoutPathPrefix?: string
  ecommConfirmPathPrefix?: string
}>) => {
  const {
    selectedLocation: { shortUrl },
    ecommSettings,
    isEcommMultiLocationEnabled
  } = useRestaurant()

  // Replicate the multi-location path adjustment logic from PageShell
  // If multi-location is enabled, append the location shortUrl to the base path
  const adjustedEcommPath =
    !!ecommSettings?.enabledRestaurantGuids?.length &&
    !!shortUrl &&
    isEcommMultiLocationEnabled
      ? urljoin(ecommPath ?? '', shortUrl)
      : ecommPath

  return (
    <RestaurantRoutesContextProvider
      shortUrl={shortUrl}
      baseEcommPath={ecommPath}
      ecommCheckoutPathPrefix={ecommCheckoutPathPrefix}
      ecommConfirmPathPrefix={ecommConfirmPathPrefix}
      ecommPath={adjustedEcommPath}
    >
      <ReservationContextProvider>
        <GlobalNavContextProvider>
          <EcommPageContexts>
            {/* LocationSelectionGate is also mounted in PageShell.
                These two placements are mutually exclusive: EcommerceProvidersWrapper
                handles ecommerce routes while PageShell handles standard OO pages. */}
            <LocationSelectionGate>{children}</LocationSelectionGate>
          </EcommPageContexts>
        </GlobalNavContextProvider>
      </ReservationContextProvider>
    </RestaurantRoutesContextProvider>
  )
}

/**
 * Wrapper component that provides all necessary contexts for ecommerce routes.
 * By wrapping all ecommerce routes at the routing level, these providers stay mounted
 * during navigation between ecommerce pages, preventing redundant API calls and
 * maintaining state properly.
 *
 * Architecture:
 * - Provides ALL contexts that PageShell normally provides (Spotlight, Attribution, Debug, Restaurant, etc.)
 * - RestaurantContextProvider comes BEFORE OO ApolloProvider so it can access the Sites Apollo client
 * - RestaurantContextProvider is passed currentPageType={PageType.Ecommerce} for alternate isEcomm signal
 * - OO-specific contexts (Cart, Delivery, Offers, Loyalty, Fulfillment, etc.) use the OO Apollo client
 * - Ecommerce routes use EcommercePageLayout for layout (Nav/Footer) instead of PageShell
 * - This prevents duplicate providers and keeps OO contexts mounted across navigation
 * - Multi-location support: Adjusts ecommPath to include location shortUrl when enabled
 * - Includes EcommPageContexts, which provides the minimum contexts required by EcommPage itself.
 *   The editor satisfies this same requirement by rendering EcommPageContexts directly in EcommPage.
 */
export const EcommerceProvidersWrapper = ({
  ecommPath,
  ecommCheckoutPathPrefix,
  ecommConfirmPathPrefix,
  children
}: React.PropsWithChildren<Props>) => {
  return (
    <SpotlightContextProvider>
      <AttributionContextProvider>
        <DebugContextProvider>
          <RestaurantContextProvider
            ooUsage={RxDataUsage.Required}
            sitesUsage={RxDataUsage.Required}
            isEditor={false}
            currentPageType={PageType.Ecommerce}
            ecommPath={ecommPath}
          >
            <EcommerceRoutesProvider
              ecommPath={ecommPath}
              ecommCheckoutPathPrefix={ecommCheckoutPathPrefix}
              ecommConfirmPathPrefix={ecommConfirmPathPrefix}
            >
              {children}
            </EcommerceRoutesProvider>
          </RestaurantContextProvider>
        </DebugContextProvider>
      </AttributionContextProvider>
    </SpotlightContextProvider>
  )
}
